/**
 * History / My Hours Dashboard — Personal hours exploration dashboard.
 *
 * Features:
 * - Interactive histogram showing total hours per month (vertical bars)
 * - Stacked category breakdown on hover/drill-in
 * - Category filter buttons (not dropdown)
 * - Date range presets
 * - Full-width responsive layout
 * - Paginated entry list below
 */

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useAuth } from '../hooks/useAuth';
import { apiFetch } from '../lib/api';
import { useDashboard } from '../hooks/useDashboard';
import { useHoursSummary } from '../hooks/useHoursSummary';
import type { DashboardMonth, GroupBy } from '../hooks/useDashboard';
import AppLayout from '../components/layout/AppLayout';
import TimeEntryList from '../components/TimeEntryList';
import type { TimeEntry } from '../components/TimeEntryList';
import type { CategoryId } from '@shared/categories';

// ── Types ────────────────────────────────────────────────────────────────

interface HistoryResponse {
  data: TimeEntry[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    total_pages: number;
  };
}

type DatePreset = 'this_month' | 'last_month' | 'this_year' | 'all_time' | 'custom';

type ViewRange = 12 | 24 | 36 | 60;

const CATEGORY_COLORS: Record<string, string> = {
  operational: 'oklch(0.600 0.150 165)',
  administrative: 'oklch(0.650 0.155 240)',
  training: 'oklch(0.750 0.183 75)',
  public_event: 'oklch(0.700 0.170 330)',
};

const CATEGORY_LABELS: Record<string, string> = {
  operational: 'Operational',
  administrative: 'Administrative',
  training: 'Training',
  public_event: 'Public Event',
};

const ALL_CATEGORIES: CategoryId[] = ['operational', 'administrative', 'training', 'public_event'];

// ── Helpers ──────────────────────────────────────────────────────────────

function getPresetLabel(preset: DatePreset): string {
  switch (preset) {
    case 'this_month':
      return 'This Month';
    case 'last_month':
      return 'Last Month';
    case 'this_year':
      return 'This Year';
    case 'all_time':
      return 'All Time';
    case 'custom':
      return 'Custom';
  }
}

function toDateInputValue(date: Date): string {
  return date.toISOString().slice(0, 10);
}

function formatPeriodLabel(key: string): string {
  // Year: "2024"
  if (/^\d{4}$/.test(key)) return key;
  // Quarter: "2024-Q1"
  if (/^\d{4}-Q\d$/.test(key)) return key.replace('-', ' ');
  // Day: "2024-03-15"
  if (/^\d{4}-\d{2}-\d{2}$/.test(key)) {
    const d = new Date(key + 'T00:00:00Z');
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
  }
  // Month: "2024-03"
  const [y, m] = key.split('-');
  const date = new Date(parseInt(y), parseInt(m) - 1, 1);
  return date.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
}

function formatPeriodLabelFull(key: string): string {
  if (/^\d{4}$/.test(key)) return key;
  if (/^\d{4}-Q\d$/.test(key)) {
    const [y, q] = key.split('-');
    return `${q.replace('Q', 'Quarter ')} ${y}`;
  }
  if (/^\d{4}-\d{2}-\d{2}$/.test(key)) {
    const d = new Date(key + 'T00:00:00Z');
    return d.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      timeZone: 'UTC',
    });
  }
  const [y, m] = key.split('-');
  const date = new Date(parseInt(y), parseInt(m) - 1, 1);
  return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
}

// ── Histogram Component ──────────────────────────────────────────────────

function Histogram({
  data,
  activeCategories,
  onBarClick,
}: {
  data: DashboardMonth[];
  activeCategories: Set<string>;
  onBarClick?: (month: string) => void;
}) {
  const [hoveredIdx, setHoveredIdx] = useState<number | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Filter data based on active categories
  const filteredData = useMemo(() => {
    return data.map((m) => {
      if (activeCategories.size === ALL_CATEGORIES.length) return m;
      const filteredCats: Record<string, number> = {};
      let total = 0;
      for (const [cat, hours] of Object.entries(m.categories)) {
        if (activeCategories.has(cat)) {
          filteredCats[cat] = hours;
          total += hours;
        }
      }
      return { ...m, categories: filteredCats, total_hours: Math.round(total * 100) / 100 };
    });
  }, [data, activeCategories]);

  const maxHours = useMemo(() => {
    return Math.max(1, ...filteredData.map((m) => m.total_hours));
  }, [filteredData]);

  // Determine label interval: show every Nth label based on bar count
  const labelInterval = useMemo(() => {
    const count = filteredData.length;
    if (count <= 12) return 1;
    if (count <= 24) return 2;
    if (count <= 36) return 3;
    if (count <= 60) return 6;
    if (count <= 180) return 14; // ~every 2 weeks for day view
    return 30; // ~monthly labels for day view of large ranges
  }, [filteredData.length]);

  // Scroll to end (most recent) on mount/data change
  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollLeft = containerRef.current.scrollWidth;
    }
  }, [data]);

  if (data.length === 0) {
    return (
      <div className="w-full h-[240px] flex items-center justify-center text-content-tertiary text-sm">
        No data available
      </div>
    );
  }

  // Fit bars to container when there are few; scroll when there are many.
  // The threshold and minimum bar width adapt to keep the chart readable.
  const barCount = filteredData.length;
  const useScroll = barCount > 36;
  const minBarWidth = useScroll ? Math.max(4, Math.min(16, Math.floor(800 / barCount))) : undefined;
  const innerMinWidth = useScroll ? `${barCount * (minBarWidth! + 2)}px` : undefined;

  return (
    <div className="w-full">
      {/* Y-axis labels + chart area */}
      <div className="flex">
        {/* Y-axis */}
        <div className="flex flex-col justify-between pr-2 text-[10px] text-content-tertiary h-[200px] shrink-0">
          <span>{Math.ceil(maxHours)}h</span>
          <span>{Math.ceil(maxHours / 2)}h</span>
          <span>0h</span>
        </div>

        {/* Scrollable bar area */}
        <div ref={containerRef} className="flex-1 overflow-x-auto" style={{ scrollbarWidth: 'thin' }}>
          <div
            className="flex items-end gap-[2px] h-[200px]"
            style={innerMinWidth ? { minWidth: innerMinWidth } : undefined}
          >
            {filteredData.map((month, idx) => {
              const barHeight = maxHours > 0 ? (month.total_hours / maxHours) * 100 : 0;
              const isHovered = hoveredIdx === idx;
              const cats = Object.entries(month.categories).sort(([a], [b]) => a.localeCompare(b));

              return (
                <div
                  key={month.month}
                  className="flex-1 flex flex-col items-center relative cursor-pointer"
                  style={minBarWidth ? { minWidth: `${minBarWidth}px` } : undefined}
                  onMouseEnter={() => setHoveredIdx(idx)}
                  onMouseLeave={() => setHoveredIdx(null)}
                  onClick={() => onBarClick?.(month.month)}
                >
                  {/* Tooltip */}
                  {isHovered && (
                    <div className="absolute bottom-full mb-2 z-20 bg-surface-overlay border border-edge-default rounded-lg px-3 py-2 shadow-xl whitespace-nowrap">
                      <div className="text-xs font-bold text-content-primary mb-1">
                        {formatPeriodLabelFull(month.month)}
                      </div>
                      <div className="text-xs font-semibold text-action-primary">{month.total_hours}h total</div>
                      {cats.map(([cat, hours]) => (
                        <div key={cat} className="flex items-center gap-1.5 mt-0.5">
                          <div
                            className="w-2 h-2 rounded-full shrink-0"
                            style={{ background: CATEGORY_COLORS[cat] ?? '#666' }}
                          />
                          <span className="text-[10px] text-content-secondary">
                            {CATEGORY_LABELS[cat] ?? cat}: {hours}h
                          </span>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Stacked bar */}
                  <div
                    className="w-full rounded-t-md overflow-hidden transition-all duration-200"
                    style={{
                      height: `${barHeight}%`,
                      minHeight: month.total_hours > 0 ? '4px' : '0px',
                      opacity: isHovered ? 1 : 0.85,
                      transform: isHovered ? 'scaleY(1.02)' : 'scaleY(1)',
                      transformOrigin: 'bottom',
                    }}
                  >
                    {cats.length > 0 ? (
                      <div className="flex flex-col-reverse w-full h-full">
                        {cats.map(([cat, hours]) => {
                          const segmentPct = month.total_hours > 0 ? (hours / month.total_hours) * 100 : 0;
                          return (
                            <div
                              key={cat}
                              style={{
                                height: `${segmentPct}%`,
                                background: CATEGORY_COLORS[cat] ?? '#666',
                                minHeight: hours > 0 ? '2px' : '0px',
                              }}
                            />
                          );
                        })}
                      </div>
                    ) : (
                      <div className="w-full h-full" style={{ background: 'oklch(0.600 0.150 165)' }} />
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* X-axis labels — show every Nth label to prevent overflow */}
          <div className="flex gap-[2px] mt-1.5" style={innerMinWidth ? { minWidth: innerMinWidth } : undefined}>
            {filteredData.map((month, idx) => (
              <div
                key={month.month}
                className="flex-1 text-center text-[10px] text-content-tertiary truncate"
                style={minBarWidth ? { minWidth: `${minBarWidth}px` } : undefined}
              >
                {idx % labelInterval === 0 ? formatPeriodLabel(month.month) : ''}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Stats Cards ──────────────────────────────────────────────────────────

function StatsCards({ data, activeCategories }: { data: DashboardMonth[]; activeCategories: Set<string> }) {
  const stats = useMemo(() => {
    let totalHours = 0;
    const catTotals: Record<string, number> = {};

    for (const month of data) {
      for (const [cat, hours] of Object.entries(month.categories)) {
        if (!activeCategories.has(cat)) continue;
        totalHours += hours;
        catTotals[cat] = (catTotals[cat] ?? 0) + hours;
      }
    }

    const avgPerMonth = data.length > 0 ? totalHours / data.length : 0;

    return { totalHours: Math.round(totalHours * 10) / 10, avgPerMonth: Math.round(avgPerMonth * 10) / 10, catTotals };
  }, [data, activeCategories]);

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      <StatCard label="Total Hours" value={`${stats.totalHours}h`} />
      <StatCard label="Monthly Avg" value={`${stats.avgPerMonth}h`} />
      {Object.entries(stats.catTotals)
        .sort(([, a], [, b]) => b - a)
        .slice(0, 2)
        .map(([cat, hours]) => (
          <StatCard
            key={cat}
            label={CATEGORY_LABELS[cat] ?? cat}
            value={`${Math.round(hours * 10) / 10}h`}
            color={CATEGORY_COLORS[cat]}
          />
        ))}
    </div>
  );
}

function StatCard({ label, value, color }: { label: string; value: string; color?: string }) {
  return (
    <div className="bg-surface-raised border border-edge-muted rounded-xl px-4 py-3">
      <div className="text-[11px] font-semibold text-content-tertiary uppercase tracking-wider mb-1">{label}</div>
      <div className="text-xl font-bold" style={{ color: color ?? 'var(--interactive-primary)' }}>
        {value}
      </div>
    </div>
  );
}

// ── Main Component ───────────────────────────────────────────────────────

export default function History() {
  const { user } = useAuth();

  // Current month summary
  const summary = useHoursSummary();

  // Dashboard histogram data
  const [viewRange, setViewRange] = useState<ViewRange>(12);
  const [groupBy, setGroupBy] = useState<GroupBy>('month');
  const dashboard = useDashboard(viewRange, groupBy);

  // Category filter (buttons, not dropdown)
  const [activeCategories, setActiveCategories] = useState<Set<string>>(new Set(ALL_CATEGORIES));

  // Entry list filters
  const [preset, setPreset] = useState<DatePreset>('this_month');
  const [customStart, setCustomStart] = useState(() => {
    const d = new Date();
    d.setDate(1);
    return toDateInputValue(d);
  });
  const [customEnd, setCustomEnd] = useState(() => toDateInputValue(new Date()));
  const [listCategoryFilter, setListCategoryFilter] = useState<CategoryId | ''>('');

  // Entry list data
  const [entries, setEntries] = useState<TimeEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [total, setTotal] = useState(0);

  const toggleCategory = useCallback((cat: string) => {
    setActiveCategories((prev) => {
      const next = new Set(prev);
      if (next.has(cat)) {
        if (next.size > 1) next.delete(cat);
      } else {
        next.add(cat);
      }
      return next;
    });
  }, []);

  const handleBarClick = useCallback((key: string) => {
    let startDate: Date;
    let endDate: Date;

    if (/^\d{4}$/.test(key)) {
      // Year: "2024"
      const y = parseInt(key, 10);
      startDate = new Date(y, 0, 1);
      endDate = new Date(y, 11, 31);
    } else if (/^\d{4}-Q(\d)$/.test(key)) {
      // Quarter: "2024-Q1"
      const y = parseInt(key.slice(0, 4), 10);
      const q = parseInt(key.slice(6), 10);
      startDate = new Date(y, (q - 1) * 3, 1);
      endDate = new Date(y, q * 3, 0);
    } else if (/^\d{4}-\d{2}-\d{2}$/.test(key)) {
      // Day: "2024-03-15"
      startDate = new Date(key + 'T00:00:00');
      endDate = new Date(key + 'T00:00:00');
    } else {
      // Month: "2024-03"
      const [y, m] = key.split('-');
      startDate = new Date(parseInt(y), parseInt(m) - 1, 1);
      endDate = new Date(parseInt(y), parseInt(m), 0);
    }

    setPreset('custom');
    setCustomStart(toDateInputValue(startDate));
    setCustomEnd(toDateInputValue(endDate));
  }, []);

  const fetchEntries = useCallback(
    async (targetPage: number) => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        params.set('page', String(targetPage));
        params.set('limit', '20');
        params.set('sort', 'clock_in');
        params.set('order', 'desc');

        if (preset === 'all_time') {
          // No date filter
        } else if (preset !== 'custom') {
          params.set('preset', preset);
        } else {
          if (customStart) params.set('start_date', `${customStart}T00:00:00Z`);
          if (customEnd) params.set('end_date', `${customEnd}T23:59:59Z`);
        }

        if (listCategoryFilter) {
          params.set('category', listCategoryFilter);
        }

        const data = await apiFetch<HistoryResponse>(`/api/punch/history?${params.toString()}`);
        setEntries(data.data);
        setTotalPages(data.pagination.total_pages);
        setTotal(data.pagination.total);
        setPage(data.pagination.page);
      } catch {
        setEntries([]);
        setTotalPages(0);
        setTotal(0);
      } finally {
        setLoading(false);
      }
    },
    [preset, customStart, customEnd, listCategoryFilter],
  );

  useEffect(() => {
    fetchEntries(1);
  }, [fetchEntries]);

  return (
    <AppLayout title="My Hours">
      <div className="w-full">
        {/* Dashboard Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mt-4 mb-6">
          <div>
            <h1 className="text-xl font-bold text-content-primary m-0">My Hours</h1>
            <p className="text-sm text-content-secondary mt-0.5 m-0">
              Track and explore your time across all categories
            </p>
          </div>
        </div>

        {/* Current Month Summary */}
        {summary.data && !summary.loading && (
          <div className="w-full p-3.5 bg-surface-raised rounded-xl border border-edge-muted mb-4">
            <div className="flex justify-between items-baseline mb-2.5">
              <span className="text-xs font-semibold text-content-secondary uppercase tracking-wider">
                {summary.data.period.label}
              </span>
              <span className="text-lg font-bold text-action-primary">{summary.data.total_hours}h</span>
            </div>
            {summary.data.categories.length > 0 ? (
              <div className="flex flex-col gap-1">
                {summary.data.categories.map((cat) => (
                  <div key={cat.category} className="flex justify-between items-center">
                    <span className="text-xs text-content-secondary">
                      {cat.category
                        .split('_')
                        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
                        .join(' ')}
                    </span>
                    <span className="text-xs font-semibold text-content-primary">{cat.total_hours}h</span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-xs text-content-tertiary">No hours recorded this month.</div>
            )}
          </div>
        )}

        {/* Stats Cards */}
        {dashboard.data && <StatsCards data={dashboard.data.months} activeCategories={activeCategories} />}

        {/* Histogram Section */}
        <div className="mt-6 bg-surface-raised border border-edge-muted rounded-xl p-4 md:p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
            <h2 className="text-sm font-bold text-content-primary m-0">Hours Over Time</h2>
            <div className="flex flex-wrap gap-3">
              {/* Group by toggle */}
              <div className="flex gap-1 items-center">
                <span className="text-[10px] text-content-tertiary uppercase tracking-wider mr-1">View</span>
                {(['day', 'month', 'quarter', 'year'] as GroupBy[]).map((g) => (
                  <button
                    key={g}
                    type="button"
                    onClick={() => setGroupBy(g)}
                    className={`px-2 py-1 text-[11px] rounded-md cursor-pointer border transition-colors duration-150 ${
                      groupBy === g
                        ? 'font-bold bg-action-primary text-white border-transparent'
                        : 'font-medium text-content-secondary bg-transparent border-edge-default hover:bg-surface-sunken'
                    }`}
                  >
                    {g.charAt(0).toUpperCase() + g.slice(1)}
                  </button>
                ))}
              </div>
              {/* View range buttons */}
              <div className="flex gap-1 items-center">
                <span className="text-[10px] text-content-tertiary uppercase tracking-wider mr-1">Range</span>
                {([12, 24, 36, 60] as ViewRange[]).map((r) => (
                  <button
                    key={r}
                    type="button"
                    onClick={() => setViewRange(r)}
                    className={`px-2.5 py-1 text-[11px] rounded-md cursor-pointer border transition-colors duration-150 ${
                      viewRange === r
                        ? 'font-bold bg-action-primary text-white border-transparent'
                        : 'font-medium text-content-secondary bg-transparent border-edge-default hover:bg-surface-sunken'
                    }`}
                  >
                    {r === 12 ? '1Y' : r === 24 ? '2Y' : r === 36 ? '3Y' : '5Y'}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Category filter buttons */}
          <div className="flex flex-wrap gap-2 mb-4">
            {ALL_CATEGORIES.map((cat) => {
              const isActive = activeCategories.has(cat);
              return (
                <button
                  key={cat}
                  type="button"
                  onClick={() => toggleCategory(cat)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg cursor-pointer border transition-colors duration-150 ${
                    isActive
                      ? 'font-semibold border-transparent text-white'
                      : 'font-normal text-content-tertiary bg-transparent border-edge-default'
                  }`}
                  style={isActive ? { background: CATEGORY_COLORS[cat] } : undefined}
                >
                  {!isActive && (
                    <div className="w-2.5 h-2.5 rounded-full opacity-40" style={{ background: CATEGORY_COLORS[cat] }} />
                  )}
                  {CATEGORY_LABELS[cat]}
                </button>
              );
            })}
          </div>

          {/* Histogram */}
          {dashboard.loading ? (
            <div className="h-[200px] flex items-center justify-center text-content-tertiary text-sm">
              Loading chart...
            </div>
          ) : dashboard.data ? (
            <Histogram data={dashboard.data.months} activeCategories={activeCategories} onBarClick={handleBarClick} />
          ) : (
            <div className="h-[200px] flex items-center justify-center text-content-tertiary text-sm">
              {dashboard.error ?? 'No data'}
            </div>
          )}
        </div>

        {/* Entry List Section */}
        <div className="mt-6">
          <h2 className="text-sm font-bold text-content-primary mb-3 m-0">Time Entries</h2>

          {/* Filters row */}
          <div className="flex flex-col sm:flex-row gap-3 mb-4">
            {/* Date presets */}
            <div className="flex gap-1.5 flex-wrap">
              {(['this_month', 'last_month', 'this_year', 'all_time', 'custom'] as DatePreset[]).map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => setPreset(p)}
                  className={`px-3 py-1.5 text-xs rounded-md cursor-pointer border transition-colors duration-150 ${
                    preset === p
                      ? 'font-bold bg-action-primary text-white border-transparent'
                      : 'font-normal text-content-secondary bg-surface-raised border-edge-default hover:bg-surface-sunken'
                  }`}
                >
                  {getPresetLabel(p)}
                </button>
              ))}
            </div>

            {/* Category filter buttons for entry list */}
            <div className="flex gap-1.5 flex-wrap">
              <button
                type="button"
                onClick={() => setListCategoryFilter('')}
                className={`px-3 py-1.5 text-xs rounded-md cursor-pointer border transition-colors duration-150 ${
                  listCategoryFilter === ''
                    ? 'font-bold bg-action-primary text-white border-transparent'
                    : 'font-normal text-content-secondary bg-surface-raised border-edge-default hover:bg-surface-sunken'
                }`}
              >
                All
              </button>
              {ALL_CATEGORIES.map((cat) => (
                <button
                  key={cat}
                  type="button"
                  onClick={() => setListCategoryFilter(cat)}
                  className={`px-3 py-1.5 text-xs rounded-md cursor-pointer border transition-colors duration-150 ${
                    listCategoryFilter === cat
                      ? 'font-bold text-white border-transparent'
                      : 'font-normal text-content-secondary bg-surface-raised border-edge-default hover:bg-surface-sunken'
                  }`}
                  style={listCategoryFilter === cat ? { background: CATEGORY_COLORS[cat] } : undefined}
                >
                  {CATEGORY_LABELS[cat]}
                </button>
              ))}
            </div>
          </div>

          {/* Custom date inputs */}
          {preset === 'custom' && (
            <div className="flex gap-2.5 mb-4 max-w-sm">
              <div className="flex-1">
                <label htmlFor="custom-start" className="block text-xs text-content-tertiary mb-1">
                  Start
                </label>
                <input
                  id="custom-start"
                  type="date"
                  value={customStart}
                  onChange={(e) => setCustomStart(e.target.value)}
                  className="w-full px-2.5 py-2 text-sm bg-surface-raised border border-edge-default rounded-md text-content-primary outline-none"
                />
              </div>
              <div className="flex-1">
                <label htmlFor="custom-end" className="block text-xs text-content-tertiary mb-1">
                  End
                </label>
                <input
                  id="custom-end"
                  type="date"
                  value={customEnd}
                  onChange={(e) => setCustomEnd(e.target.value)}
                  className="w-full px-2.5 py-2 text-sm bg-surface-raised border border-edge-default rounded-md text-content-primary outline-none"
                />
              </div>
            </div>
          )}

          {/* Result count */}
          <div className="mb-2.5 text-xs text-content-tertiary">
            {loading ? 'Loading...' : `${total} ${total === 1 ? 'entry' : 'entries'} found`}
          </div>

          {/* Entry list */}
          <TimeEntryList
            entries={entries}
            loading={loading}
            emptyMessage="No entries for this period."
            userRole={user?.role ?? 'member'}
            onEntryUpdated={(updated) => {
              setEntries((prev) => prev.map((e) => (e.id === updated.id ? updated : e)));
            }}
            onEntryVoided={(entryId) => {
              setEntries((prev) => prev.filter((e) => e.id !== entryId));
              setTotal((prev) => prev - 1);
            }}
          />

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex justify-center items-center gap-4 mt-4">
              <button
                type="button"
                disabled={page <= 1}
                onClick={() => fetchEntries(page - 1)}
                className={`px-4 py-2 text-sm border border-edge-default rounded-md ${
                  page <= 1
                    ? 'bg-surface-raised text-content-tertiary cursor-default'
                    : 'bg-surface-sunken text-content-primary cursor-pointer'
                }`}
              >
                Prev
              </button>
              <span className="text-xs text-content-secondary">
                Page {page} of {totalPages}
              </span>
              <button
                type="button"
                disabled={page >= totalPages}
                onClick={() => fetchEntries(page + 1)}
                className={`px-4 py-2 text-sm border border-edge-default rounded-md ${
                  page >= totalPages
                    ? 'bg-surface-raised text-content-tertiary cursor-default'
                    : 'bg-surface-sunken text-content-primary cursor-pointer'
                }`}
              >
                Next
              </button>
            </div>
          )}
        </div>
      </div>
    </AppLayout>
  );
}
